import { useState } from 'react'
import { useVoiceCall } from '../contexts/voice-call-context'
import { useVoiceCallSession } from '../hooks/use-voice-call-session'

/**
 * Soft presence when this Cognito user is in a live call on another device
 * (Q1C). No Chime join until Switch (Q2C).
 */
export function ActiveElsewhereBanner() {
  const session = useVoiceCallSession()
  const { switchToThisDevice, dismissActiveElsewhere } = useVoiceCall()
  const [switching, setSwitching] = useState(false)
  const [error, setError] = useState<string | null>(null)

  if (!session || session.phase !== 'elsewhere') return null

  const peerName = session.isGroup
    ? session.groupTitle ?? 'Group call'
    : session.peer.displayName
  const platformLabel =
    session.activeDevicePlatform === 'ios'
      ? 'iPhone'
      : session.activeDevicePlatform === 'android'
        ? 'Android'
        : session.activeDevicePlatform === 'web'
          ? 'another browser'
          : 'another device'

  const onSwitch = async () => {
    setError(null)
    setSwitching(true)
    try {
      await switchToThisDevice()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Switch failed')
    } finally {
      setSwitching(false)
    }
  }

  return (
    <div
      className="pointer-events-none fixed inset-x-0 top-[max(0.5rem,var(--safe-top))] z-[60] flex justify-center px-4"
      role="status"
      aria-live="polite"
    >
      <div className="pointer-events-auto flex max-w-lg flex-col gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm text-white shadow-lg ring-1 ring-white/10">
        <p className="font-semibold leading-snug">
          You&apos;re already in a call on {platformLabel}
        </p>
        <p className="text-xs text-white/75">
          {peerName} — media stays on that device until you switch.
        </p>
        {error && (
          <p className="text-xs text-rose-300" role="alert">
            {error}
            <button
              type="button"
              className="ml-2 underline focus:outline-none focus-visible:ring-2 focus-visible:ring-white"
              onClick={() => void onSwitch()}
              aria-label="Retry switching the call to this device"
            >
              Retry
            </button>
          </p>
        )}
        <div className="flex flex-wrap items-center gap-2 pt-0.5">
          <button
            type="button"
            onClick={() => void onSwitch()}
            disabled={switching}
            className="rounded-full bg-emerald-500 px-4 py-1.5 text-sm font-semibold text-white transition hover:bg-emerald-600 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
            aria-label={
              switching
                ? 'Switching call to this device'
                : 'Switch call to this device'
            }
          >
            {switching ? 'Switching…' : 'Switch'}
          </button>
          <button
            type="button"
            onClick={() => dismissActiveElsewhere()}
            disabled={switching}
            className="rounded-full bg-white/10 px-4 py-1.5 text-sm font-medium text-white/90 transition hover:bg-white/20 focus:outline-none focus-visible:ring-2 focus-visible:ring-white disabled:opacity-60"
            aria-label="Dismiss call-on-another-device notice"
          >
            Dismiss
          </button>
        </div>
      </div>
    </div>
  )
}
